import { useState, useEffect } from 'react'
import { Cookie } from 'lucide-react'

export type CookiePreference = 'all' | 'essential' | null

const STORAGE_KEY = 'cookie_consent'

export function getCookiePreference(): CookiePreference {
  try {
    const val = localStorage.getItem(STORAGE_KEY)
    if (val === 'all' || val === 'essential') return val
  } catch {}
  return null
}

export function setCookiePreference(pref: 'all' | 'essential') {
  try {
    localStorage.setItem(STORAGE_KEY, pref)
  } catch {}
}

export function hasSentryConsent(): boolean {
  return getCookiePreference() === 'all'
}

interface CookieConsentProps {
  onCookiePolicy?: () => void
}

export default function CookieConsent({ onCookiePolicy }: CookieConsentProps) {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (getCookiePreference() === null) {
      // Small delay so it doesn't flash on page load
      const timer = setTimeout(() => setVisible(true), 800)
      return () => clearTimeout(timer)
    }
  }, [])

  const handleAcceptAll = () => {
    setCookiePreference('all')
    setVisible(false)
    // Enable Sentry dynamically if DSN is available
    initSentryIfConsented()
  }

  const handleEssentialOnly = () => {
    setCookiePreference('essential')
    setVisible(false)
  }

  if (!visible) return null

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6">
      <div className="max-w-2xl mx-auto bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-2xl shadow-2xl p-5 md:p-6">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 bg-brand-500/10 rounded-xl flex items-center justify-center shrink-0 mt-0.5">
            <Cookie className="w-5 h-5 text-brand-500" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">
              Cookie Preferences
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
              We use <strong>essential cookies</strong> for authentication and security.
              With your permission, we also use <strong>Sentry</strong> for error tracking to improve the service.
              {onCookiePolicy && (
                <>
                  {' '}Read our{' '}
                  <button
                    onClick={onCookiePolicy}
                    className="text-brand-700 hover:text-brand-800 underline font-medium"
                  >
                    Cookie Policy
                  </button>{' '}
                  for details.
                </>
              )}
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleAcceptAll}
                className="px-5 py-2.5 bg-brand-700 hover:bg-brand-800 text-white text-sm font-medium rounded-xl transition-colors"
              >
                Accept All
              </button>
              <button
                onClick={handleEssentialOnly}
                className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 dark:bg-dark-surface-2 dark:hover:bg-dark-border text-gray-700 dark:text-gray-300 text-sm font-medium rounded-xl transition-colors"
              >
                Essential Only
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/** Initialize Sentry at runtime if user consented */
export function initSentryIfConsented() {
  if (!hasSentryConsent()) return

  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!dsn || !import.meta.env.PROD) return

  // Dynamic import so Sentry isn't loaded if user declined
  import('@sentry/react').then((Sentry) => {
    // Only init if not already initialized
    if (!Sentry.getClient()) {
      Sentry.init({
        dsn,
        tracesSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
        sendDefaultPii: false,
        environment: 'production',
      })
    }
  }).catch(() => {
    // Silently fail — Sentry is non-essential
  })
}
